/**
 * Date helpers for working with date-only strings (YYYY-MM-DD).
 *
 * Background: Postgres `date` columns return calendar dates as plain "YYYY-MM-DD"
 * strings with no time and no timezone. The naive `new Date("2026-07-09")` parses
 * that as UTC midnight, which then displays as Jul 8 in any timezone west of UTC.
 *
 * These helpers always interpret date-only strings as LOCAL calendar dates.
 * Use them anywhere a trip/item date is parsed, displayed, compared, or counted.
 */

/**
 * Parse a "YYYY-MM-DD" date-only string into a Date at LOCAL midnight.
 * Avoids the UTC parsing trap of `new Date("2026-07-09")`.
 *
 * Accepts: "2026-07-09", "2026-07-09T12:00:00", "2026-07-09T00:00:00.000Z",
 *          undefined, null, "" (returns null for invalid input).
 */
export function parseLocalDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;
  const datePart = String(dateStr).split('T')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/**
 * Format a date-only string for display.
 * Always interprets the input as a local calendar date.
 * Returns '' if input is missing or invalid.
 */
export function formatDate(
  dateStr: string | undefined | null,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string {
  const d = parseLocalDate(dateStr);
  return d ? d.toLocaleDateString('en-US', options) : '';
}

/**
 * Format a date range. Returns "Dates TBD" if both are missing,
 * a single formatted date if only start is present,
 * otherwise "start - end".
 */
export function formatDateRange(
  startStr: string | undefined | null,
  endStr: string | undefined | null,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
): string {
  if (!startStr && !endStr) return 'Dates TBD';
  if (!endStr) return formatDate(startStr, options);
  return `${formatDate(startStr, options)} - ${formatDate(endStr, options)}`;
}

/**
 * Convert a Date object to "YYYY-MM-DD" using LOCAL year/month/day.
 * Use for itinerary day keys, CSV filenames, anywhere a date-only string is needed.
 * Avoids `.toISOString().split('T')[0]` which converts back to UTC.
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Today's date as "YYYY-MM-DD" in local time.
 * Use for date input min/max attributes and "today" comparisons.
 */
export function todayLocalString(): string {
  return toLocalDateString(new Date());
}

/**
 * Number of days inclusive between two date-only strings.
 * "2026-07-09" to "2026-07-13" returns 5.
 * Returns 0 if either input is missing or invalid.
 */
export function daysBetween(
  startStr: string | undefined | null,
  endStr: string | undefined | null
): number {
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (!start || !end) return 0;
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
}